/**
 * RFQ Quantity Options Hook
 * 
 * Manages quantity options for specifications within an RFQ. This allows
 * clients to request bids for multiple quantity scenarios, enabling vendors
 * to provide volume-based pricing and helping clients evaluate cost trade-offs.
 * 
 * **Key Features:**
 * - Fetch quantity options for a specific RFQ
 * - Save multiple quantity options per specification
 * - Delete individual quantity options
 * - Automatic refresh when RFQ changes
 * - Support for specification-specific quantities
 * 
 * **Workflow:**
 * 1. Client creates RFQ with specifications
 * 2. Client adds quantity options (e.g., 100, 500, 1000 units)
 * 3. Vendors bid with different pricing for each quantity tier
 * 4. Client selects optimal quantity/price combination
 * 
 * **Data Structure:**
 * - Each option links to a specification within the RFQ
 * - Multiple quantity options can exist per specification
 * - Options are deleted and re-created on save (batch upsert)
 * 
 * @param {string} rfqId - The RFQ ID to fetch quantity options for
 * 
 * @returns {Object} Quantity options management
 * @returns {RFQQuantityOption[]} quantityOptions - Array of quantity options
 * @returns {boolean} loading - Loading state for fetch operations
 * @returns {string | null} error - Error message if operation failed
 * @returns {Function} saveQuantityOptions - Save multiple quantity options
 * @returns {Function} deleteQuantityOption - Delete a single option
 * 
 * @example
 * // Manage quantity options in RFQ form
 * const {
 *   quantityOptions,
 *   loading,
 *   saveQuantityOptions,
 *   deleteQuantityOption
 * } = useRFQQuantityOptions(rfqId);
 * 
 * // Save quantity tiers for a specification
 * await saveQuantityOptions(rfqId, [
 *   { specification_id: 'spec-1', quantity: 100, unit: 'each' },
 *   { specification_id: 'spec-1', quantity: 500, unit: 'each' },
 *   { specification_id: 'spec-1', quantity: 1000, unit: 'each' }
 * ]);
 * 
 * @example
 * // Remove a quantity option
 * await deleteQuantityOption(optionId);
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface RFQQuantityOption {
  id: string;
  rfq_id: string;
  specification_id: string;
  quantity_option: number;
  unit: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface QuantityOptionsInput {
  specification_id: string;
  quantities: number[];
  unit: string;
}

export const useRFQQuantityOptions = (rfqId?: string) => {
  const [quantityOptions, setQuantityOptions] = useState<RFQQuantityOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchQuantityOptions = async () => {
    if (!rfqId) return;
    
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('rfq_specification_quantity_options')
        .select('*')
        .eq('rfq_id', rfqId)
        .order('specification_id')
        .order('quantity_option');

      if (fetchError) throw fetchError;
      setQuantityOptions(data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch quantity options';
      setError(message);
      console.error('Error fetching quantity options:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveQuantityOptions = async (rfqId: string, inputs: QuantityOptionsInput[]) => {
    try {
      setLoading(true);
      setError(null);

      // First, delete all existing options for this RFQ
      const specIds = inputs.map(i => i.specification_id);
      await supabase
        .from('rfq_specification_quantity_options')
        .delete()
        .eq('rfq_id', rfqId)
        .in('specification_id', specIds);

      // Then insert new options
      const optionsToInsert = inputs.flatMap(input =>
        input.quantities.map(qty => ({
          rfq_id: rfqId,
          specification_id: input.specification_id,
          quantity_option: qty,
          unit: input.unit || 'each'
        }))
      );

      if (optionsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('rfq_specification_quantity_options')
          .insert(optionsToInsert);

        if (insertError) throw insertError;
      }

      await fetchQuantityOptions();
      
      toast({
        title: "Success",
        description: "Quantity options saved successfully"
      });

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save quantity options';
      setError(message);
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteQuantityOption = async (optionId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('rfq_specification_quantity_options')
        .delete()
        .eq('id', optionId);

      if (deleteError) throw deleteError;

      await fetchQuantityOptions();
      
      toast({
        title: "Success",
        description: "Quantity option deleted"
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete quantity option';
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    }
  };

  useEffect(() => {
    if (rfqId) {
      fetchQuantityOptions();
    }
  }, [rfqId]);

  return {
    quantityOptions,
    loading,
    error,
    fetchQuantityOptions,
    saveQuantityOptions,
    deleteQuantityOption
  };
};
